/**
 * Types pour le module StockOut (Sorties de Stock)
 * Pertes, dons, prélèvements personnels, sponsoring, etc.
 */

export enum StockOutType {
  // Pertes
  DLC_EXPIRED = 'DLC_EXPIRED',           // Date limite dépassée
  DAMAGE = 'DAMAGE',                     // Casse physique
  QUALITY = 'QUALITY',                   // Qualité insuffisante (flétri, abîmé)
  SHRINKAGE = 'SHRINKAGE',               // Vol ou écart inventaire

  // Valorisation externe
  DONATION = 'DONATION',                 // Don sans contrepartie (attestation fiscale)
  SPONSORING = 'SPONSORING',             // Sponsoring avec contrepartie (charge publicitaire)
  RETURN_SUPPLIER = 'RETURN_SUPPLIER',   // Retour accepté par fournisseur

  // Prélèvements internes
  PERSONAL_USE = 'PERSONAL_USE',         // Prélèvement exploitant
  STAFF_MEAL = 'STAFF_MEAL',             // Consommation employé (avantage en nature)
  STAFF_PURCHASE = 'STAFF_PURCHASE',     // Achat employé à prix réduit

  // Autres
  SAMPLE = 'SAMPLE',                     // Échantillon client / dégustation
  EXPIRED_UNSOLD = 'EXPIRED_UNSOLD',     // Invendu fin de journée
}

export enum BeneficiaryType {
  OWNER = 'OWNER',             // Exploitant (Philippe)
  FAMILY = 'FAMILY',           // Famille exploitant
  ASSOCIATE = 'ASSOCIATE',     // Associé (Paul)
  EMPLOYEE = 'EMPLOYEE',       // Salarié
  ASSOCIATION = 'ASSOCIATION', // Association / banque alimentaire
  EVENT = 'EVENT',             // Événement sponsorisé
  CUSTOMER = 'CUSTOMER',       // Client (échantillon)
  SUPPLIER = 'SUPPLIER',       // Fournisseur (retour)
}

/**
 * Métadonnées pour chaque type de sortie (affichage UI)
 */
export interface StockOutTypeInfo {
  type: StockOutType;
  label: string;
  icon: string;
  color: string;         // Couleur TailwindCSS (ex: 'red', 'green')
  category: 'loss' | 'external' | 'internal' | 'other';
  requiresBeneficiary: boolean;
  requiresSupplier: boolean;
  vatApplicable: boolean;
}

export const STOCK_OUT_TYPE_INFO: Record<StockOutType, StockOutTypeInfo> = {
  // Pertes
  [StockOutType.DLC_EXPIRED]: {
    type: StockOutType.DLC_EXPIRED,
    label: 'DLC dépassée',
    icon: '⏰',
    color: 'red',
    category: 'loss',
    requiresBeneficiary: false,
    requiresSupplier: false,
    vatApplicable: false,
  },
  [StockOutType.DAMAGE]: {
    type: StockOutType.DAMAGE,
    label: 'Casse',
    icon: '💔',
    color: 'red',
    category: 'loss',
    requiresBeneficiary: false,
    requiresSupplier: false,
    vatApplicable: false,
  },
  [StockOutType.QUALITY]: {
    type: StockOutType.QUALITY,
    label: 'Qualité',
    icon: '🥀',
    color: 'orange',
    category: 'loss',
    requiresBeneficiary: false,
    requiresSupplier: false,
    vatApplicable: false,
  },
  [StockOutType.SHRINKAGE]: {
    type: StockOutType.SHRINKAGE,
    label: 'Écart/Vol',
    icon: '❓',
    color: 'gray',
    category: 'loss',
    requiresBeneficiary: false,
    requiresSupplier: false,
    vatApplicable: false,
  },

  // Valorisation externe
  [StockOutType.DONATION]: {
    type: StockOutType.DONATION,
    label: 'Don',
    icon: '🎁',
    color: 'green',
    category: 'external',
    requiresBeneficiary: true,
    requiresSupplier: false,
    vatApplicable: false,
  },
  [StockOutType.SPONSORING]: {
    type: StockOutType.SPONSORING,
    label: 'Sponsoring',
    icon: '🏆',
    color: 'blue',
    category: 'external',
    requiresBeneficiary: true,
    requiresSupplier: false,
    vatApplicable: false,
  },
  [StockOutType.RETURN_SUPPLIER]: {
    type: StockOutType.RETURN_SUPPLIER,
    label: 'Retour fournisseur',
    icon: '↩️',
    color: 'purple',
    category: 'external',
    requiresBeneficiary: false,
    requiresSupplier: true,
    vatApplicable: false,
  },

  // Prélèvements internes
  [StockOutType.PERSONAL_USE]: {
    type: StockOutType.PERSONAL_USE,
    label: 'Prélèvement perso',
    icon: '👤',
    color: 'yellow',
    category: 'internal',
    requiresBeneficiary: true,
    requiresSupplier: false,
    vatApplicable: true,
  },
  [StockOutType.STAFF_MEAL]: {
    type: StockOutType.STAFF_MEAL,
    label: 'Repas employé',
    icon: '🍽️',
    color: 'yellow',
    category: 'internal',
    requiresBeneficiary: true,
    requiresSupplier: false,
    vatApplicable: true,
  },
  [StockOutType.STAFF_PURCHASE]: {
    type: StockOutType.STAFF_PURCHASE,
    label: 'Achat employé',
    icon: '💳',
    color: 'yellow',
    category: 'internal',
    requiresBeneficiary: true,
    requiresSupplier: false,
    vatApplicable: false,
  },

  // Autres
  [StockOutType.SAMPLE]: {
    type: StockOutType.SAMPLE,
    label: 'Échantillon',
    icon: '🧪',
    color: 'cyan',
    category: 'other',
    requiresBeneficiary: false,
    requiresSupplier: false,
    vatApplicable: false,
  },
  [StockOutType.EXPIRED_UNSOLD]: {
    type: StockOutType.EXPIRED_UNSOLD,
    label: 'Invendu fin de journée',
    icon: '🌙',
    color: 'gray',
    category: 'other',
    requiresBeneficiary: false,
    requiresSupplier: false,
    vatApplicable: false,
  },
};

/**
 * Bénéficiaires prédéfinis pour les prélèvements personnels
 */
export const PREDEFINED_BENEFICIARIES = [
  { type: BeneficiaryType.OWNER, name: 'Philippe', label: 'Philippe' },
  { type: BeneficiaryType.OWNER, name: 'Sandrine', label: 'Sandrine' },
  { type: BeneficiaryType.ASSOCIATE, name: 'Paul', label: 'Paul' },
  { type: BeneficiaryType.FAMILY, name: 'Famille', label: 'Famille' },
];

/**
 * Interface pour un StockOut
 */
export interface StockOut {
  id: string;
  tenant_id: string;
  reference: string;
  recorded_at: string;
  recorded_by?: string;
  recorded_by_name?: string;
  /**
   * 'validated' | 'cancelled'. Une sortie annulée n'est jamais supprimée : elle
   * sort des rapports mais reste au registre — un registre de démarque qu'on peut
   * effacer n'a aucune valeur devant un contrôle.
   */
  status?: string;

  // Produit
  product_id: string;
  product_name?: string;
  lot_id?: string;
  lot_number?: string;
  quantity: number;
  unit?: string;

  // Type et motif
  type: StockOutType;
  reason?: string;

  // Bénéficiaire
  beneficiary_type?: BeneficiaryType;
  beneficiary_name?: string;

  // Valorisation
  cost_price_cents: number;
  sale_price_cents: number;

  // TVA
  vat_applicable: boolean;
  vat_rate?: number;
  vat_amount_cents?: number;

  // Fournisseur
  supplier_name?: string;
  return_ref?: string;

  // Sponsoring
  event_name?: string;
  sponsor_value_cents?: number;
  counterpart_desc?: string;

  // Documentation
  photo_url?: string;
  document_url?: string;

  created_at: string;
  updated_at: string;
}

/**
 * DTO pour créer un StockOut
 */
export interface CreateStockOutDto {
  product_id: string;
  lot_id?: string;
  quantity: number;
  unit?: string;
  type: StockOutType;
  reason?: string;
  beneficiary_type?: BeneficiaryType;
  beneficiary_name?: string;
  supplier_name?: string;
  return_ref?: string;
  event_name?: string;
  sponsor_value_cents?: number;
  counterpart_desc?: string;
  photo_url?: string;
  document_url?: string;
  recorded_by?: string;
  recorded_by_name?: string;
}

/**
 * Sortie de stock EN LOT depuis un panier (perte / prélèvement par panier).
 * type / motif / bénéficiaire communs à tout le lot ; seules product_id + quantity varient.
 */
export interface StockOutBatchLine {
  product_id: string;
  quantity: number;
  unit?: string;
}

export interface CreateStockOutBatchDto {
  type: StockOutType;
  reason?: string;
  beneficiary_type?: BeneficiaryType;
  beneficiary_name?: string;
  supplier_name?: string;
  return_ref?: string;
  event_name?: string;
  sponsor_value_cents?: number;
  counterpart_desc?: string;
  photo_url?: string;
  document_url?: string;
  recorded_by?: string;
  recorded_by_name?: string;
  lines: StockOutBatchLine[];
}

export interface StockOutBatchResultLine {
  product_id: string;
  product_name: string;
  requested: number;
  recorded: number;
  unit: string | null;
  sale_price_cents: number;
  warning?: string;
}

export interface StockOutBatchResult {
  reference: string;
  type: StockOutType;
  recorded_at: string;
  total_sale_price_cents: number;
  lines: StockOutBatchResultLine[];
}

/**
 * Filtres pour la liste des StockOut
 */
export interface StockOutFilter {
  type?: StockOutType;
  startDate?: string;
  endDate?: string;
  productId?: string;
  limit?: number;
  offset?: number;
}

/**
 * Résultat paginé de la liste des sorties.
 * `total` est le nombre RÉEL sur la période, indépendant du plafond d'affichage :
 * sans lui, une liste tronquée passerait pour une liste complète.
 */
export interface StockOutListResult {
  items: StockOut[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * Résultat d'une annulation de sortie.
 * `approximate` = la sortie était antérieure au rattachement des mouvements :
 * le stock a été rendu au rayon faute de savoir d'où il venait → à vérifier.
 */
export interface DeleteStockOutResult {
  success: boolean;
  approximate: boolean;
  restored: Array<{ lot_id: string; location: string; quantity: number }>;
}

/**
 * Rapport de pertes
 */
export interface LossesReport {
  period: {
    start: string;
    end: string;
  };
  total_count: number;
  total_cost_cents: number;
  total_sale_cents: number;
  by_type: Array<{
    type: StockOutType;
    count: number;
    total_cost_cents: number;
    total_sale_cents: number;
  }>;
  top_products: Array<{
    product_id: string;
    product_name: string;
    total_quantity: number;
    total_cost_cents: number;
    unit?: string;
  }>;
  /**
   * Pertes encodées depuis la FICHE PRODUIT (« Compter ce produit »). Elles
   * n'écrivent pas dans `stock_outs` : sans ce bloc, elles n'apparaissaient nulle
   * part. `loss_cost_cents` ne retient que les motifs décrivant une disparition de
   * marchandise ; « erreur de saisie » et « erreur de réception » corrigent une
   * donnée fausse et ne sont pas de la démarque.
   */
  adjustments?: {
    count: number;
    total_cost_cents: number;
    loss_cost_cents: number;
    correction_cost_cents: number;
    by_reason: Array<{
      reason: string;
      count: number;
      total_cost_cents: number;
      total_quantity: number;
      is_loss: boolean;
    }>;
  };
  /** Sorties de stock + pertes de fiche, corrections de saisie exclues. */
  combined_cost_cents?: number;
}

/**
 * Un comptage « Compter ce produit » à la BAISSE, ligne par ligne (onglet Détail
 * de Sorties de stock, 22/10/2026). Il ajuste le lot au lieu de créer une sortie ;
 * il s'annule pendant 7 jours (écriture inverse côté serveur) et sort alors de la
 * démarque. Règle serveur : `common/comptages-fiche.ts`.
 */
export interface ComptageFiche {
  /** Identifiant du mouvement de stock (celui qu'on annule). */
  id: string;
  date: string;
  product_id: string;
  product_name: string;
  lot_id: string | null;
  lot_number: string | null;
  /** Quantité retirée, positive. */
  quantite: number;
  unite: string | null;
  emplacement: 'rayon' | 'reserve';
  motif: string;
  remarque: string | null;
  photo_url: string | null;
  cout_cents: number;
  en_demarque: boolean;
  par: string | null;
  annule: boolean;
  annulable: boolean;
}

export interface ComptagesFicheResult {
  items: ComptageFiche[];
  total: number;
  /** La liste s'arrête à 500 lignes : réduire la période pour tout voir. */
  tronque: boolean;
}

/**
 * Rapport de prélèvements personnels (pour TVA)
 */
export interface PersonalUseReport {
  period: {
    start: string;
    end: string;
  };
  items: Array<{
    id: string;
    recorded_at: string;
    product_name: string;
    quantity: number;
    unit?: string;
    beneficiary_name: string;
    sale_price_cents: number;
    vat_rate: number;
    vat_amount_cents: number;
  }>;
  totals: {
    total_sale_cents: number;
    total_vat_cents: number;
    by_vat_rate: Record<string, { base_cents: number; vat_cents: number }>;
  };
}
